/**
 * A task's attachments — Aaron, Oct 26 ("there needs to be an attachments
 * section. I would suggest showing the first few and if they upload a ton of
 * documents, have an expand message to show the rest so we don't clutter up
 * the primary task screen"); `docs/activity.md` §8.7.
 *
 * Planner's order: under the checklist, over the history, so the sheet's
 * layout above is as it was. The first three as rows — a thumbnail or the
 * file's glyph, the name (opens it), *PDF · 240 KB*, × — then **Show N
 * more**, which opens the rest in place, and **Show fewer**. **Attach a file**
 * at the foot is the paperclip: the picker, and the section is a drop target
 * on a desktop — as is the rest of the sheet, so a drop that misses the
 * section by an inch is not the browser opening the file over the draft.
 *
 * The picker, the drop and the per-file try are `useRecordUpload`'s — the one
 * owner of every drop — pointed at the task (`filesAt`). On a task that
 * exists each file is linked as it lands and × takes it off (the link's
 * `deleted_at`; the file stays); on New task (`NewTaskFiles`) the files only
 * go up, and ride on the create as `fileIds`. Done, the rows are only read.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  MAX_TASK_FILES, NEW_TASK_MAX_FILES, TASK_FILES_SHOWN, taskFileMeta, taskFilesFoldWords, tooManyTaskFilesWords,
} from '@rowted/shared'
import type { UploadedFile } from '../../lib/api.js'
import { cn } from '../../lib/cn.js'
import { usePhone } from '../../lib/use-narrow.js'
import { useRecordUpload, type RecordUpload } from '../../record/use-record-upload.js'
import { activityRequest } from '../api.js'
import { Glyph } from '../glyphs.js'

/** One file as the section draws it — the server's `RecordFile`, as much as is drawn. */
export interface TaskFileView {
  id: string
  name: string
  contentType: string
  byteSize: number
  /** Where it opens: through the task (`/api/v1/activity/tasks/:id/files/:fileId`), or as uploaded on New task. */
  url: string
}

/** A task with no files — one array, so the sheet's `?? NO_TASK_FILES` never reads as a change. */
export const NO_TASK_FILES: readonly TaskFileView[] = []

const errorOf = (cause: unknown): string => (cause instanceof Error ? cause.message : String(cause)) || 'That didn’t save.'

const viewOf = (file: TaskFileView): TaskFileView => ({
  id: file.id, name: file.name, contentType: file.contentType, byteSize: file.byteSize, url: file.url,
})

/**
 * A file dropped anywhere on the sheet lands in its Attachments. Without
 * this a drop that misses the section by an inch is the browser's: it opens
 * the file over the page, and the sheet's unsaved draft goes with it. The
 * section's own drop says so first (`defaultPrevented`), so a file is never
 * sent twice; anything that is not files is left alone.
 */
function useSheetDrop(upload: RecordUpload, sheet: string, on: boolean): void {
  const { add } = upload
  useEffect(() => {
    if (!on) return undefined
    const onSheet = (event: DragEvent) => event.target instanceof Element
      && event.target.closest(`[role="dialog"][aria-label="${sheet}"]`) !== null
    const carriesFiles = (event: DragEvent) => Array.from(event.dataTransfer?.types ?? []).includes('Files')
    const over = (event: DragEvent) => { if (onSheet(event) && carriesFiles(event)) event.preventDefault() }
    const drop = (event: DragEvent) => {
      if (event.defaultPrevented || !onSheet(event) || !event.dataTransfer?.files.length) return
      event.preventDefault()
      void add(event.dataTransfer.files)
    }
    window.addEventListener('dragover', over)
    window.addEventListener('drop', drop)
    return () => {
      window.removeEventListener('dragover', over)
      window.removeEventListener('drop', drop)
    }
  }, [add, sheet, on])
}

/** The section on a task that exists: each file linked as it lands. */
export function TaskFiles({
  taskId, initial, readOnly = false,
}: {
  taskId: string
  /** The sheet's read of them (`TaskSheetAnswer.files`). */
  initial: readonly TaskFileView[]
  /** A finished task's files are only read and opened (Board 19 D keeps a done sheet short). */
  readOnly?: boolean
}) {
  const [files, setFiles] = useState<TaskFileView[]>(() => initial.map(viewOf))
  const [problem, setProblem] = useState<string | null>(null)
  const base = `/activity/tasks/${taskId}/files`
  /* A reload of the sheet brings the server's files; the rows draw them. */
  useEffect(() => { setFiles(initial.map(viewOf)) }, [initial])

  const onFiles = useCallback((items: TaskFileView[]) => setFiles(items.map(viewOf)), [])
  const upload = useRecordUpload({
    filesAt: base, orgUnitId: '', canEdit: !readOnly, describe: false, onFiles,
  })
  useSheetDrop(upload, 'Task', !readOnly)

  const remove = async (file: TaskFileView) => {
    const before = files
    setFiles(files.filter((one) => one.id !== file.id))
    setProblem(null)
    try {
      const answer = await activityRequest<{ items: TaskFileView[] }>(`${base}/${file.id}/remove`, { method: 'POST', body: {} })
      setFiles(answer.items.map(viewOf))
    } catch (cause) {
      setProblem(errorOf(cause))
      activityRequest<{ items: TaskFileView[] }>(base)
        .then((answer) => setFiles(answer.items.map(viewOf)))
        .catch(() => setFiles(before))
    }
  }

  if (readOnly && files.length === 0) return null
  return (
    <TaskFilesSection files={files} upload={upload} readOnly={readOnly} max={MAX_TASK_FILES}
      problem={problem} onRemove={(file) => void remove(file)}
      heading />
  )
}

/**
 * New task's: the same section, the files only uploaded and handed to the
 * sheet, which sends their ids with the create — twenty at most, the
 * create's own limit (`NEW_TASK_MAX_FILES`).
 */
export function NewTaskFiles({
  files, onChange,
}: { files: readonly TaskFileView[]; onChange: (files: TaskFileView[]) => void }) {
  const [problem, setProblem] = useState<string | null>(null)
  const onUploaded = useCallback((made: UploadedFile[]) => {
    const room = NEW_TASK_MAX_FILES - files.length
    const fresh = made.filter((one) => !files.some((had) => had.id === one.id))
    setProblem(fresh.length > room ? tooManyTaskFilesWords(NEW_TASK_MAX_FILES) : null)
    onChange([...fresh.slice(0, Math.max(0, room)).reverse(), ...files])
  }, [files, onChange])
  const upload = useRecordUpload({ filesAt: null, orgUnitId: '', canEdit: true, describe: false, onUploaded })
  useSheetDrop(upload, 'New task', true)
  return (
    <TaskFilesSection files={[...files]} upload={upload} readOnly={false} max={NEW_TASK_MAX_FILES} problem={problem}
      onRemove={(file) => { setProblem(null); onChange(files.filter((one) => one.id !== file.id)) }} />
  )
}

/** The drawing both share. */
function TaskFilesSection({
  files, upload, readOnly, max, problem, onRemove, heading = false,
}: {
  files: TaskFileView[]
  upload: RecordUpload
  readOnly: boolean
  max: number
  problem: string | null
  onRemove: (file: TaskFileView) => void
  /** The Task sheet's own label row; New task's is its `Field`'s. */
  heading?: boolean
}) {
  const phone = usePhone()
  const [open, setOpen] = useState(false)
  const fold = taskFilesFoldWords(files.length, open)
  const shown = open ? files : files.slice(0, TASK_FILES_SHOWN)
  const full = files.length >= max
  const error = problem ?? upload.error

  return (
    <div data-task-files {...(readOnly ? {} : upload.dropProps)}
      className={cn(upload.over && 'rounded-[10px] outline-dashed outline-2 outline-offset-4 outline-accent-line')}>
      {!readOnly && <input {...upload.inputProps} />}
      {heading && (
        <div className="mb-1.5 flex items-center text-[12px] font-semibold text-text-muted">
          Attachments
          {files.length > 0 && (
            <span className="tnum ml-auto font-semibold text-text-faint">
              {files.length === 1 ? '1 file' : `${files.length} files`}
            </span>
          )}
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-col rounded-[10px] border border-line bg-surface">
          {shown.map((file) => (
            <FileRow key={file.id} file={file} phone={phone} readOnly={readOnly} onRemove={() => onRemove(file)} />
          ))}
          {fold && (
            <button type="button" aria-expanded={open} onClick={() => setOpen(!open)}
              className={cn('flex w-full items-center gap-1.5 rounded-b-card border-t border-line-faint px-2.5 text-left text-[12.5px] font-semibold text-accent hover:bg-hover',
                phone ? 'min-h-[44px]' : 'min-h-[34px]')}>
              {fold}
            </button>
          )}
        </div>
      )}
      {!readOnly && !full && (
        <button type="button" onClick={() => upload.openPicker()} disabled={upload.busy}
          className={cn('flex w-full items-center gap-2 rounded-lg px-2 text-left text-[13px] font-semibold text-accent hover:bg-field disabled:opacity-60',
            files.length > 0 && 'mt-1.5', phone ? 'h-11' : 'h-9')}>
          <Glyph name="paperclip" size={14} />
          {upload.over ? 'Drop to attach' : 'Attach a file'}
          {!phone && !upload.over && <span className="font-normal text-text-faint">or drop it here</span>}
        </button>
      )}
      {!readOnly && full && (
        <div className="mt-1.5 text-[12px] text-text-muted">{tooManyTaskFilesWords(max)}</div>
      )}
      {upload.busy && upload.progress && (
        <div role="status" className="mt-1.5 truncate text-[12px] text-text-muted">
          Uploading {upload.progress.done + 1} of {upload.progress.total} · {upload.progress.name}
        </div>
      )}
      {error && <div role="alert" className="mt-1.5 text-[12px] text-danger">{error}</div>}
    </div>
  )
}

/** A photo is its own thumbnail; a document its glyph — red for a PDF, as the New sheets' chips draw one. */
function FileLead({ file, phone }: { file: TaskFileView; phone: boolean }) {
  const box = phone ? 'h-7 w-7' : 'h-6 w-6'
  if (file.contentType.startsWith('image/')) {
    return <img src={file.url} alt="" loading="lazy" className={cn('shrink-0 rounded-[5px] bg-surface-alt object-cover', box)} />
  }
  const glyph = file.contentType.startsWith('video/') ? 'video' : file.contentType.startsWith('audio/') ? 'mic' : 'file'
  return (
    <span aria-hidden className={cn('inline-flex shrink-0 items-center justify-center', box,
      file.contentType === 'application/pdf' ? 'text-danger' : 'text-text-muted')}>
      <Glyph name={glyph} size={phone ? 16 : 14} />
    </span>
  )
}

function FileRow({ file, phone, readOnly, onRemove }: {
  file: TaskFileView; phone: boolean; readOnly: boolean; onRemove: () => void
}) {
  return (
    <div data-task-file={file.name}
      className={cn('group flex items-center gap-2 border-b border-line-faint px-2 last:border-b-0',
        phone ? 'min-h-[48px]' : 'min-h-[38px]')}>
      <FileLead file={file} phone={phone} />
      {/* Opens it to view, in a tab of its own — a photo or a PDF shows in the page (`inline`). */}
      <a href={file.url} target="_blank" rel="noopener noreferrer" title={file.name}
        className={cn('min-w-0 flex-1 truncate font-medium text-text hover:text-accent hover:underline',
          /* The row's whole height on a phone, so the name is a 48 px target. */
          phone ? 'py-3.5 text-[15px] leading-5' : 'py-1 text-[13px]')}>
        {file.name}
      </a>
      <span className="tnum shrink-0 text-[11.5px] text-text-faint">{taskFileMeta(file.name, file.contentType, file.byteSize)}</span>
      {!readOnly && (
        <button type="button" aria-label={`Remove ${file.name}`} title="Take it off the task" onClick={onRemove}
          className={cn('inline-flex shrink-0 items-center justify-center rounded text-text-muted hover:text-text',
            phone ? 'h-11 w-11' : 'h-7 w-6 opacity-0 focus-visible:opacity-100 group-hover:opacity-100')}>
          <Glyph name="close" size={13} />
        </button>
      )}
    </div>
  )
}
